import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import { Box, Divider, LinearProgress, Typography } from "@mui/material";
import ListIcon from '@mui/icons-material/List';
import { StandardPage, StandardPageTopContent, StandardPageMainContent, MuiCol, MuiRow } from "../../../components/layout";
import { useAppContext } from "../../../components/context/appContext";
import { ScannerInput, MuiTextField } from '../../../components/inputs';
import { MuiButton } from "../../../components/mui";
import palletApi from "../../../api/palletApi";
import shipmentApi from "../../../api/shipmentApi";
import masterpackApi from "../../../api/masterpackApi";
import poApi from "../../../api/poApi";
import cameraModelApi from "../../../api/cameraModelApi";
import plantApi from "../../../api/plantApi";
import netsuiteApi from "../../../api/netsuiteApi";
import { getCurrentAuthUser } from "../../../utils/authorization";
import LooseItemDialog from "./forms/looseItemEntry";
import ShipmentListDialog from "./forms/shipmentSelection";

// ---------------------------------------------------------------------------
// Reducers
//
// This screen only ever adds, removes, or wholesale-replaces (on load) the
// pallets/masterpacks arrays — no PO/CI/cost editing happens here.
// ---------------------------------------------------------------------------

function palletsReducer(state, action) {
  switch (action.type) {
    case "ADD_PALLET":
      return [...state, action.payload];
    case "REMOVE_PALLET":
      return state.filter(pallet => pallet.palletNumber !== action.palletNumber);
    case "RESET_PALLETS":
      return action.payload;
    default:
      return state;
  }
}

function masterpacksReducer(state, action) {
  switch (action.type) {
    case "ADD_MASTERPACK":
      return [...state, action.payload];
    case "REMOVE_MASTERPACK":
      return state.filter(mp => mp.masterpackNumber !== action.masterpackNumber);
    case "RESET_MASTERPACKS":
      return action.payload;
    default:
      return state;
  }
}


// ---------------------------------------------------------------------------
// Data transforms (see note at top of file)
//
// PO IDENTIFIERS — two distinct values, do not conflate:
//   poId     — NetSuite internal ID, e.g. "10132368". This is what the
//              cameras/items API returns, under a field it calls `poNumber`.
//              That upstream field name is a misnomer; the value is an ID.
//   poNumber — human-readable PO number, e.g. "PO2211002245". Filled in later
//              by the PO number loader; the transforms leave it blank.
// ---------------------------------------------------------------------------

function transformPalletData(apiResponse) {
  const poMap = {};

  for (const camera of apiResponse.cameras) {
    // camera.poNumber holds the PO internal ID despite its name — see above.
    const poId = camera.poNumber;
    const masterPack = apiResponse.masterPacks.find(mp => mp.id === camera.masterPackId);
    const productCode = masterPack?.cameraModel?.productCode ?? null;

    if (!poMap[poId]) poMap[poId] = {};
    if (!poMap[poId][productCode]) poMap[poId][productCode] = 0;
    poMap[poId][productCode]++;
  }

  const pos = Object.entries(poMap).map(([poId, skuMap]) => ({
    poId,
    poNumber: "",
    cameraModel: Object.entries(skuMap).map(([itemSku, quantity]) => ({
      itemNetsuiteId: null,
      itemSku,
      quantity,
      ciNumber: "",
    })),
  }));

  return {
    palletNumber: apiResponse.barcode,
    status: "standby",
    pos,
  };
}

function transformMasterpackData(apiResponse) {
  const poMap = {};

  for (const item of apiResponse.items) {
    // item.poNumber holds the PO internal ID despite its name — see above.
    const poId = item.poNumber;
    const sku = item.cameraModel;
    if (!poMap[poId]) poMap[poId] = {};
    if (!poMap[poId][sku]) poMap[poId][sku] = 0;
    poMap[poId][sku]++;
  }

  const pos = Object.entries(poMap).map(([poId, skuMap]) => ({
    poId,
    poNumber: "",
    cameraModel: Object.entries(skuMap).map(([itemSku, quantity]) => ({
      itemNetsuiteId: null,
      itemSku,
      quantity,
      ciNumber: "",
    })),
  }));

  return {
    masterpackNumber: apiResponse.barcode,
    status: "standby",
    pos,
  };
}


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Total camera/article count across all POs and SKUs for one container. */
function countCameras(container) {
  return container.pos.reduce(
    (sum, po) => sum + po.cameraModel.reduce((s, cam) => s + cam.quantity, 0),
    0
  );
}

/**
 * Adds one loose line to the looseItems array, merging into the existing
 * entry for that PO when there is one. A repeat of the same PO + SKU adds to
 * the quantity already there rather than creating a duplicate line, which is
 * what NetSuite would otherwise receive as two IR lines for one item.
 */
function addLooseLine(looseItems, line) {
  const { poId, poNumber, itemNetsuiteId, itemSku, quantity } = line;

  const existingEntry = looseItems.find(entry => entry.poId === poId);

  if (!existingEntry) {
    return [...looseItems, {
      poId,
      poNumber: poNumber ?? "",
      lines: [{ itemNetsuiteId, itemSku, quantity, ciNumber: "" }],
    }];
  }

  return looseItems.map(entry => {
    if (entry.poId !== poId) return entry;

    const existingLine = (entry.lines || []).find(l => l.itemSku === itemSku);

    return {
      ...entry,
      lines: existingLine
        ? entry.lines.map(l =>
            l.itemSku !== itemSku ? l : { ...l, quantity: l.quantity + quantity }
          )
        : [...(entry.lines || []), { itemNetsuiteId, itemSku, quantity, ciNumber: "" }],
    };
  });
}

/** Removes one PO + SKU line, dropping the PO entry once it has no lines left. */
function removeLooseLine(looseItems, poId, itemSku) {
  return looseItems
    .map(entry =>
      entry.poId !== poId ? entry : {
        ...entry,
        lines: (entry.lines || []).filter(l => l.itemSku !== itemSku),
      }
    )
    .filter(entry => (entry.lines || []).length > 0);
}

/** Flattens looseItems into one display row per PO + SKU. */
function flattenLooseItems(looseItems) {
  const rows = [];
  for (const entry of looseItems) {
    for (const line of (entry.lines || [])) {
      rows.push({
        poId: entry.poId,
        poNumber: entry.poNumber,
        itemSku: line.itemSku,
        quantity: line.quantity,
      });
    }
  }
  return rows;
}

/** Works out what a raw scan refers to, so the loading message can say so. */
function classifyScan(scan) {
  if (/^PL-[A-Z0-9]+$/.test(scan)) return "pallet";
  if (/^\d{10,16}$/.test(scan)) return "masterpack";
  return "shipment";
}


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ContainerRow({ identifier, cameraCount, onRemove, editable, t }) {
  return (
    <MuiRow spacing={1} sx={{ alignItems: "center", py: 1, borderBottom: "1px solid", borderColor: "grey.200" }}>
      <MuiCol size={{ xs: 6, md: 6 }}>
        <Typography fontWeight={600} sx={{ fontFamily: "monospace" }}>
          {identifier}
        </Typography>
      </MuiCol>
      <MuiCol size={{ xs: 3, md: 3 }} sx={{ textAlign: "right" }}>
        <Typography component="span" fontWeight={600}>{cameraCount}</Typography>{" "}
        <Typography component="span" variant="caption" color="text.secondary">
          {t("shipping_cm.cameras")}
        </Typography>
      </MuiCol>
      <MuiCol size={{ xs: 3, md: 3 }} sx={{ textAlign: "right" }}>
        <MuiButton
          danger
          disabled={!editable}
          onClick={onRemove}
          sx={{ py: 0.25, minHeight: 0, fontSize: 11 }}
        >
          {t("shipping_cm.remove")}
        </MuiButton>
      </MuiCol>
    </MuiRow>
  );
}

function ContainerListSection({ title, countLabel, containers, getIdentifier, onRemove, editable, leftActions, actions, t }) {
  return (
    <Box sx={{ mb: 3 }}>
      {(leftActions || actions) && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            {leftActions}
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            {actions}
          </Box>
        </Box>
      )}
      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {countLabel}
        </Typography>
      </Box>
      {containers.length > 0 && (
        <Box sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 1, px: 2 }}>
          {containers.map(container => {
            const identifier = getIdentifier(container);
            return (
              <ContainerRow
                key={identifier}
                identifier={identifier}
                cameraCount={countCameras(container)}
                onRemove={() => onRemove(identifier)}
                editable={editable}
                t={t}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}

/**
 * Loose items have no container identifier, so each row is keyed by PO + SKU
 * rather than by a barcode. Otherwise this mirrors ContainerListSection.
 */
function LooseItemListSection({ rows, onRemove, editable, t }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
          {t("shipping_cm.loose_items")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("shipping_cm.loose_items_on_shipment", { count: rows.length })}
        </Typography>
      </Box>
      {rows.length > 0 && (
        <Box sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 1, px: 2 }}>
          {rows.map(row => (
            <MuiRow
              key={`${row.poId}__${row.itemSku}`}
              spacing={1}
              sx={{ alignItems: "center", py: 1, borderBottom: "1px solid", borderColor: "grey.200" }}
            >
              <MuiCol size={{ xs: 6, md: 6 }}>
                <Typography fontWeight={600} sx={{ fontFamily: "monospace" }}>
                  {row.itemSku}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.poNumber || row.poId}
                </Typography>
              </MuiCol>
              <MuiCol size={{ xs: 3, md: 3 }} sx={{ textAlign: "right" }}>
                <Typography component="span" fontWeight={600}>{row.quantity}</Typography>
              </MuiCol>
              <MuiCol size={{ xs: 3, md: 3 }} sx={{ textAlign: "right" }}>
                <MuiButton
                  danger
                  disabled={!editable}
                  onClick={() => onRemove(row.poId, row.itemSku)}
                  sx={{ py: 0.25, minHeight: 0, fontSize: 11 }}
                >
                  {t("shipping_cm.remove")}
                </MuiButton>
              </MuiCol>
            </MuiRow>
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * Progress bar + message shown under the scanner while a scan is being
 * fetched. The box always reserves its height so the page below doesn't jump
 * every time a scan starts and finishes.
 */
function ScanProgress({ pendingScan, t }) {
  const labels = {
    pallet:     t("shipping_cm.loading_pallet",     { code: pendingScan?.code, defaultValue: "Loading pallet {{code}}…" }),
    masterpack: t("shipping_cm.loading_masterpack", { code: pendingScan?.code, defaultValue: "Loading masterpack {{code}}…" }),
    shipment:   t("shipping_cm.loading_shipment",   { code: pendingScan?.code, defaultValue: "Loading shipment {{code}}…" }),
  };

  return (
    <Box sx={{ mt: 1, minHeight: 28 }} aria-live="polite">
      {pendingScan && (
        <>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            {labels[pendingScan.type]}
          </Typography>
        </>
      )}
    </Box>
  );
}


// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function Template(props) {
  const { t } = props;
  const appContext = useAppContext();

  const [scannedValue, setScannedValue] = useState();
  const [scannerError, setScannerError] = useState();
  const [pallets, dispatch] = useReducer(palletsReducer, []);
  const [masterpacks, masterpacksDispatch] = useReducer(masterpacksReducer, []);
  const [looseItems, setLooseItems] = useState([]);
  const [isLooseDialogOpen, setIsLooseDialogOpen] = useState(false);
  const [shipment, setShipment] = useState({ shipmentCode: "", status: "Draft" });

  const [isShipmentListOpen, setIsShipmentListOpen] = useState(false);

  // What is currently being fetched after a scan: { type, code } or null.
  // The state drives the progress bar; the ref is the actual guard, because
  // it updates synchronously and can't be read stale between two fast scans.
  const [pendingScan, setPendingScan] = useState(null);
  const scanInFlightRef = useRef(false);

  // Plants, for the dialog's origin-name column, and the current user's own
  // plant location id, to restrict the list to shipments originating there.
  const [plants, setPlants] = useState([]);
  const [userOriginLocationId, setUserOriginLocationId] = useState(null);

  useEffect(() => {
    plantApi.getPlants()
      .then(result => setPlants(Array.isArray(result?.data) ? result.data : []))
      .catch(err => console.error("Failed to load plants", err));
  }, []);

  // Shipping locations, for the dialog's destination-name column. Without
  // these the dialog can only show the raw NetSuite location ID.
  const [destinations, setDestinations] = useState([]);

  useEffect(() => {
    let cancelled = false;

    netsuiteApi.getShippingLocations()
      .then(result => {
        if (cancelled) return;
        // A failed NetSuite call can still resolve with a 200 and an
        // unexpected body, which .catch() never sees. Guard the shape so
        // the dialog never receives undefined.
        const locs = Array.isArray(result?.data?.data) ? result.data.data : [];
        if (locs.length === 0) {
          console.warn("getShippingLocations(): no locations returned", result?.data);
        }
        setDestinations(locs);
      })
      .catch(err => {
        console.error("Failed to load shipping locations", err);
        if (!cancelled) setDestinations([]);
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    getCurrentAuthUser()
      .then(user => {
        if (cancelled) return;
        setUserOriginLocationId(user?.plant?.netsuiteCmId ?? null);
      })
      .catch(err => {
        console.error("Failed to resolve current user's plant", err);
        if (!cancelled) setUserOriginLocationId(null);
      });

    return () => { cancelled = true; };
  }, []);

  // Open POs on this CM, used to populate the loose item dialog's PO dropdown.
  // getOpenPoOnCm returns { poId, tranName } — the internal ID and the
  // human-readable PO number as separate fields, which is exactly the split
  // the shipment document wants. poId is left null so we get the whole list.
  const [poList, setPoList] = useState([]);
  const [poLoading, setPoLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setPoLoading(true);
      try {
        const user = await getCurrentAuthUser();
        const result = await poApi.getOpenPoOnCm(user?.plant?.netsuiteCmId);

        if (cancelled) return;

        if (result?.data?.status === "success") {
          const pos = result.data.data?.pos ?? [];
          pos.sort((a, b) =>
            a.tranName > b.tranName ? 1 : b.tranName > a.tranName ? -1 : 0
          );
          setPoList(pos);
        } else {
          console.warn("getOpenPoOnCm(): unexpected response", result?.data);
          setPoList([]);
        }
      } catch (err) {
        console.error("Failed to load open POs", err);
        if (!cancelled) setPoList([]);
      } finally {
        if (!cancelled) setPoLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // value = poId (internal ID, what gets persisted and sent to NetSuite)
  // label = tranName (human-readable PO number, display only)
  const poOptions = useMemo(() =>
    poList.map(po => ({ label: po.tranName, value: po.poId }))
  , [poList]);

  // Camera models, used to populate the loose item dialog's model dropdown
  // and to resolve a scanned UPC to a SKU.
  const [cameraModels, setCameraModels] = useState([]);

  useEffect(() => {
    let cancelled = false;

    cameraModelApi.getCameraModels()
      .then(result => {
        if (cancelled) return;
        setCameraModels(Array.isArray(result?.data) ? result.data : []);
      })
      .catch(err => {
        console.error("Failed to load camera models", err);
        if (!cancelled) setCameraModels([]);
      });

    return () => { cancelled = true; };
  }, []);

  // The dialog wants { itemSku, name, upc, itemNetsuiteId }. On a cameraModel
  // document those live under productCode, name and barcode — the barcode
  // being the ITF-14 printed on the masterpack label. productCode is what the
  // rest of the shipment pipeline calls itemSku.
  //
  // netsuiteItemId is stored as a string on cameraModels while the shipment
  // schema declares itemNetsuiteId as integer|null, so cast it the same way
  // enrichWithNetsuiteItemIds does on the API side. It is missing from some
  // documents; null is acceptable since the API resolves it again on save.
  const looseModels = useMemo(() =>
    cameraModels
      .filter(cm => cm.productCode && cm.active !== false)
      .map(cm => {
        const parsed = parseInt(cm.netsuiteItemId, 10);
        return {
          itemSku: cm.productCode,
          name: cm.name ?? null,
          upc: cm.barcode ?? null,
          itemNetsuiteId: Number.isNaN(parsed) ? null : parsed,
        };
      })
  , [cameraModels]);

  // This screen only edits an existing shipment — it never creates one (that
  // happens on the Shipping UI). Gating editable on shipmentCode means
  // scanning/loose-item entry can't build up local state against a shipment
  // that handleSave will then refuse to persist.
  const editable = !!shipment.shipmentCode && shipment.status !== 'In progress' && shipment.status !== 'Complete';

  const looseRows = flattenLooseItems(looseItems);

  function resetLocalState() {
    setShipment({ shipmentCode: "", status: "Draft" });
    dispatch({ type: "RESET_PALLETS", payload: [] });
    masterpacksDispatch({ type: "RESET_MASTERPACKS", payload: [] });
    setLooseItems([]);
  }

  async function loadShipment(code) {
    const result = await shipmentApi.getShipment(code);
    const s = result.data;
    setShipment({ shipmentCode: s.shipmentCode, status: s.status });
    dispatch({ type: "RESET_PALLETS", payload: s.pallets ?? [] });
    masterpacksDispatch({ type: "RESET_MASTERPACKS", payload: s.masterpacks ?? [] });
    setLooseItems(s.looseItems ?? []);
  }

  function scannerOnKeyDown(e) {
    if (e.key === 'Enter') {
      handleScan(e.target.value);
    }
  }

  async function handleScan(rawScan) {
    const scan = rawScan?.trim() ?? "";

    // Clear the field right away — the progress message shows the code now,
    // and the operator can see the scan was taken.
    setScannedValue("");

    if (!scan) return;

    // The input stays enabled on purpose (a disabled input loses focus and
    // the next hardware scan would go nowhere), so a scan that arrives while
    // another is still loading is rejected here instead.
    if (scanInFlightRef.current) {
      setScannerError(t("shipping_cm.scan_in_progress", { defaultValue: "Still loading the previous scan, scan again in a moment." }));
      return;
    }

    setScannerError("");

    const type = classifyScan(scan);

    // Cheap local checks first, so they fail instantly without a loading flash.
    if (type === "pallet" || type === "masterpack") {
      if (!editable) {
        setScannerError(t("shipping_cm.shipment_locked"));
        return;
      }
      const alreadyScanned = type === "pallet"
        ? pallets.some(p => p.palletNumber === scan)
        : masterpacks.some(mp => mp.masterpackNumber === scan);
      if (alreadyScanned) {
        setScannerError(t("shipping_cm.pallet_already_scanned"));
        return;
      }
    }

    scanInFlightRef.current = true;
    setPendingScan({ type, code: scan });

    try {
      if (type === "pallet") {
        // ── Pallet ────────────────────────────────────────────────────────────
        try {
          const result = await palletApi.getPallet(scan);
          dispatch({ type: "ADD_PALLET", payload: transformPalletData(result.data) });
        } catch (err) {
          setScannerError(t("shipping_cm.invalid_pallet_barcode"));
        }

      } else if (type === "masterpack") {
        // ── Masterpack ────────────────────────────────────────────────────────
        try {
          const result = await masterpackApi.getMasterpackInfo(scan);
          masterpacksDispatch({ type: "ADD_MASTERPACK", payload: transformMasterpackData(result.data) });
        } catch (err) {
          console.error("masterpack fetch error:", err?.response?.status, err?.response?.data, err);
          setScannerError(t("shipping_cm.invalid_pallet_barcode"));
        }

      } else {
        // ── Shipment code ─────────────────────────────────────────────────────
        try {
          await loadShipment(scan);
        } catch (err) {
          setScannerError(t("shipping_cm.invalid_pallet_barcode"));
        }
      }
    } finally {
      scanInFlightRef.current = false;
      setPendingScan(null);
    }
  }

  function handleRemovePallet(palletNumber) {
    dispatch({ type: "REMOVE_PALLET", palletNumber });
  }

  function handleRemoveMasterpack(masterpackNumber) {
    masterpacksDispatch({ type: "REMOVE_MASTERPACK", masterpackNumber });
  }

  function handleOpenLooseDialog() {
    setIsLooseDialogOpen(true);
  }

  function handleCloseLooseDialog() {
    setIsLooseDialogOpen(false);
  }

  function handleOpenShipmentList() {
    setIsShipmentListOpen(true);
  }

  function handleCloseShipmentList() {
    setIsShipmentListOpen(false);
  }

  // Picked a shipment from the list — close it and load that shipment, the
  // same way scanning a shipment code does.
  async function handleSelectShipment(shipmentCode) {
    setIsShipmentListOpen(false);
    try {
      await loadShipment(shipmentCode);
    } catch (err) {
      console.error(err);
      appContext.showMessage(t('error'), "An unexpected error happened.");
    }
  }

  // One loose line confirmed in the dialog. Merges into the existing entry
  // for that PO; a repeat of the same PO + SKU adds to its quantity.
  function handleAddLooseLine(line) {
    setLooseItems(prev => addLooseLine(prev, line));
    setIsLooseDialogOpen(false);
  }

  function handleRemoveLooseLine(poId, itemSku) {
    setLooseItems(prev => removeLooseLine(prev, poId, itemSku));
  }

  function handleNewShipment() {
    resetLocalState();
  }

  async function handleResetShipment() {
    if (!shipment.shipmentCode) {
      resetLocalState();
      return;
    }
    try {
      await loadShipment(shipment.shipmentCode);
    } catch (err) {
      console.error(err);
      appContext.showMessage(t('error'), "An unexpected error happened.");
    }
  }

  function buildPayload() {
    return { ...shipment, pallets, masterpacks, looseItems };
  }

  // This screen never creates a shipment — only the Shipping UI does. If
  // nothing is loaded there is nothing to save.
  async function handleSave() {
    if (!shipment.shipmentCode) {
      appContext.showMessage(t('error'), t("shipping_cm.no_shipment_to_save"));
      return;
    }
    try {
      await shipmentApi.updateShipment(shipment.shipmentCode, buildPayload());
      appContext.showMessage(t('success'), t("shipping_cm.shipment_saved", { shipmentCode: shipment.shipmentCode }));
    } catch (err) {
      console.error(err);
      appContext.showMessage(t('error'), "An unexpected error happened.");
    }
  }

  return (
    <>
      <StandardPage
        title={t("shipping_cm.shipping_cm")}
        description={t("shipping_cm.title_desc")}
        showPrinterStatus={false}
        showPrinterRFIDStatus={false}
      >
        <StandardPageTopContent>
          <MuiRow spacing={1} sx={{ alignItems: "center" }}>
            <MuiCol size="auto">
              <MuiButton startIcon={<ListIcon />} onClick={handleOpenShipmentList}>
                {t("shipping_cm.list")}
              </MuiButton>
            </MuiCol>
            <MuiCol size="grow">
              <ScannerInput
                onChange={setScannedValue}
                onKeyDown={scannerOnKeyDown}
                value={scannedValue}
                label={t("newPallet.scanMasterpack")}
                error={scannerError}
              />
            </MuiCol>
          </MuiRow>
          <ScanProgress pendingScan={pendingScan} t={t} />
        </StandardPageTopContent>
        <StandardPageMainContent>
          <MuiRow spacing={1} sx={{ mb: 3 }}>
            <MuiCol size={{ xs: 12, md: 6 }}>
              <MuiTextField
                disabled
                value={shipment.shipmentCode || t("shipping_cm.no_shipment_loaded")}
                label={t("shipping_cm.shipment_id")}
              />
            </MuiCol>
          </MuiRow>

          <Divider sx={{ mb: 3 }} />

          <ContainerListSection
            title={t("shipping_cm.pallets")}
            countLabel={t("shipping_cm.pallets_on_shipment", { count: pallets.length })}
            containers={pallets}
            getIdentifier={c => c.palletNumber}
            onRemove={handleRemovePallet}
            editable={editable}
            leftActions={
              <MuiButton disabled={!editable} onClick={handleOpenLooseDialog}>
                {t("shipping_cm.accessories_and_other")}
              </MuiButton>
            }
            actions={
              <MuiButton onClick={handleNewShipment}>
                {t("shipping_cm.clear")}
              </MuiButton>
            }
            t={t}
          />

          <ContainerListSection
            title={t("shipping_cm.masterpacks")}
            countLabel={t("shipping_cm.masterpacks_on_shipment", { count: masterpacks.length })}
            containers={masterpacks}
            getIdentifier={c => c.masterpackNumber}
            onRemove={handleRemoveMasterpack}
            editable={editable}
            t={t}
          />

          <LooseItemListSection
            rows={looseRows}
            onRemove={handleRemoveLooseLine}
            editable={editable}
            t={t}
          />

          <MuiRow sx={{ mt: 2 }}>
            <MuiCol size={{ xs: 12, md: 2 }}>
              <MuiButton onClick={handleSave} disabled={!editable}>
                {t("shipping_cm.save")}
              </MuiButton>
            </MuiCol>
          </MuiRow>
        </StandardPageMainContent>
      </StandardPage>

      <ShipmentListDialog
        isOpen={isShipmentListOpen}
        plants={plants}
        destinations={destinations}
        originLocationId={userOriginLocationId}
        onClose={handleCloseShipmentList}
        onSelect={handleSelectShipment}
      />

      <LooseItemDialog
        isOpen={isLooseDialogOpen}
        models={looseModels}
        poOptions={poOptions}
        poLoading={poLoading}
        onClose={handleCloseLooseDialog}
        onAdd={handleAddLooseLine}
      />
    </>
  );
}

Template.propTypes = {
  auth: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  auth: state.auth,
  errors: state.errors
});

export default compose(
  withTranslation(),
  connect(mapStateToProps)
)(Template);